import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Images, Loader2, PenLine, Plus, Save } from 'lucide-react'

import {
  createScriptDocument,
  patchScriptDocument,
  uploadProductionAsset,
  type Production,
  type ScriptDocument,
} from '@/api/studio-production'
import { formatResolvedApiError, resolveApiErrorMessage } from '@/features/studio/lib/resolveApiError'
import { assetMediaUrl, resolveMediaDisplayUrl } from '@/features/studio/lib/media-display'
import { AssetMultiPickerModal } from '@/features/studio/modals/AssetMultiPickerModal'
import { ScriptDocumentModalFooter } from '@/features/studio/modals/script-document-modal-footer'
import { PROJECT_TYPES } from '@/features/studio/lib/production-constants'
import { ProtoBtn, ProtoInput, ProtoLabel, ProtoModal, ProtoNumberInput, ProtoSelect, ProtoTextarea } from '@/features/studio/components/ui-kit'
import { CoverImageUpload } from '@/shared/components/CoverImageUpload'

export function ScriptDocumentModal({
  open,
  onClose,
  productionId,
  production,
  editing,
  onSaved,
  onWrite,
}: {
  open: boolean
  onClose: () => void
  productionId: string
  production?: Production | null
  editing: ScriptDocument | null
  onSaved: (doc: ScriptDocument) => void
  /** 保存后直接进入正文编辑器（仅新建或编辑后可选） */
  onWrite?: (scriptId: number) => void
}) {
  const { t } = useTranslation()
  const isEdit = editing != null
  const [title, setTitle] = useState('')
  const [summary, setSummary] = useState('')
  const [coverUrl, setCoverUrl] = useState('')
  const [episodeTarget, setEpisodeTarget] = useState(1)
  const [episodesPerSeason, setEpisodesPerSeason] = useState(0)
  const [projectType, setProjectType] = useState('comic_drama')
  const [defaultLocale, setDefaultLocale] = useState('zh-CN')
  const [pickerOpen, setPickerOpen] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    setError(null)
    if (isEdit) {
      setTitle(editing.title ?? '')
      setSummary(editing.summary ?? '')
      setCoverUrl(editing.cover_url ?? '')
      setEpisodeTarget(Math.max(1, editing.episode_target ?? 1))
      setEpisodesPerSeason(Math.max(0, editing.episodes_per_season ?? 0))
      setProjectType(editing.project_type?.trim() || production?.project_type || 'comic_drama')
      setDefaultLocale(editing.default_locale?.trim() || production?.default_locale || 'zh-CN')
      return
    }
    setTitle('')
    setSummary('')
    setCoverUrl('')
    setEpisodeTarget(1)
    setEpisodesPerSeason(0)
    setProjectType(production?.project_type?.trim() || 'comic_drama')
    setDefaultLocale(production?.default_locale?.trim() || 'zh-CN')
  }, [open, editing, isEdit, production])

  const uploadCover = useCallback(
    async (file: File) => {
      const asset = await uploadProductionAsset(productionId, file, {
        name: title.trim() || file.name,
        kind: 'misc',
        tags: ['script_cover'],
      })
      return assetMediaUrl(asset)
    },
    [productionId, title],
  )

  async function handleSave(andWrite: boolean) {
    setBusy(true)
    setError(null)
    try {
      const resolvedCover = coverUrl.trim()
      let saved: ScriptDocument
      const kind = {
        project_type: projectType,
        default_locale: defaultLocale,
      }
      if (isEdit) {
        saved = await patchScriptDocument(productionId, editing.id, {
          title: title.trim(),
          cover_url: resolvedCover,
          summary: summary.trim(),
          ...kind,
          episode_target: Math.max(1, episodeTarget),
          episodes_per_season: Math.max(0, episodesPerSeason),
        })
      } else {
        saved = await createScriptDocument(productionId, {
          title: title.trim() || undefined,
          cover_url: resolvedCover || undefined,
          summary: summary.trim() || undefined,
          ...kind,
        })
      }
      onSaved(saved)
      if (andWrite && onWrite) {
        onWrite(saved.id)
      }
      onClose()
    } catch (e) {
      setError(
        formatResolvedApiError(
          t,
          resolveApiErrorMessage(e, isEdit ? 'script.metaSaveFailed' : 'script.addScriptFailed'),
        ),
      )
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <ProtoModal
        open={open}
        onClose={() => {
          if (busy) return
          onClose()
        }}
        title={isEdit ? t('script.metaEditTitle') : t('script.addScriptTitle')}
        footer={
          <ScriptDocumentModalFooter
            layout={isEdit ? 'edit' : 'create'}
            cancel={
              <ProtoBtn
                size={isEdit ? 'md' : 'sm'}
                variant={isEdit ? 'ghost' : 'secondary'}
                disabled={busy}
                onClick={onClose}
              >
                {t('common.cancel')}
              </ProtoBtn>
            }
            actions={
              <>
                {onWrite ? (
                  <ProtoBtn
                    size={isEdit ? 'md' : 'sm'}
                    variant={isEdit ? 'soft' : 'secondary'}
                    tone={isEdit ? 'indigo' : undefined}
                    disabled={busy}
                    onClick={() => void handleSave(true)}
                  >
                    {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <PenLine className="h-3.5 w-3.5" />}
                    {t('script.metaSaveAndWrite')}
                  </ProtoBtn>
                ) : null}
                <ProtoBtn size={isEdit ? 'md' : 'sm'} disabled={busy} onClick={() => void handleSave(false)}>
                  {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : isEdit ? <Save className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />}
                  {isEdit ? t('script.metaSave') : t('script.addScriptConfirm')}
                </ProtoBtn>
              </>
            }
          />
        }
      >
        <p className="text-sm text-zinc-400">
          {isEdit ? t('script.metaEditHint') : t('script.metaCreateHint')}
        </p>

        <div className="mt-4 space-y-4">
          <div>
            <ProtoLabel required>{t('script.fieldScriptTitle')}</ProtoLabel>
            <ProtoInput
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={t('script.fieldScriptTitlePlaceholder')}
              maxLength={255}
            />
          </div>

          <div>
            <ProtoLabel>{t('script.fieldSummary')}</ProtoLabel>
            <ProtoTextarea
              rows={4}
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
              placeholder={t('script.fieldSummaryPlaceholder')}
              maxLength={8000}
            />
          </div>

          <div>
            <ProtoLabel>{t('script.fieldCover')}</ProtoLabel>
            <div className="mt-2 flex flex-wrap items-start gap-3">
              <CoverImageUpload
                value={coverUrl}
                onChange={setCoverUrl}
                onUpload={uploadCover}
                resolveDisplayUrl={resolveMediaDisplayUrl}
                disabled={busy}
              />
              <ProtoBtn size="sm" variant="secondary" disabled={busy} onClick={() => setPickerOpen(true)}>
                <Images className="h-3.5 w-3.5" />
                {t('script.coverFromAssets')}
              </ProtoBtn>
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <ProtoLabel>{t('script.fieldScriptType')}</ProtoLabel>
              <ProtoSelect value={projectType} onChange={(e) => setProjectType(e.target.value)}>
                {PROJECT_TYPES.map((k) => (
                  <option key={k} value={k}>
                    {t(`production.projectType.${k}`, { defaultValue: k })}
                  </option>
                ))}
              </ProtoSelect>
            </div>
            <div>
              <ProtoLabel>{t('script.fieldScriptLocale')}</ProtoLabel>
              <ProtoSelect value={defaultLocale} onChange={(e) => setDefaultLocale(e.target.value)}>
                <option value="zh-CN">{t('script.localeZhCN')}</option>
                <option value="en-US">{t('script.localeEnUS')}</option>
              </ProtoSelect>
            </div>
          </div>

          {isEdit ? (
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <ProtoLabel>{t('createModal.fieldEpisodes')}</ProtoLabel>
                <ProtoNumberInput
                  min={1}
                  max={999}
                  value={episodeTarget}
                  onValueChange={setEpisodeTarget}
                />
              </div>
              <div>
                <ProtoLabel>{t('createModal.fieldEpisodesPerSeason')}</ProtoLabel>
                <ProtoNumberInput
                  min={0}
                  max={999}
                  value={episodesPerSeason}
                  onValueChange={setEpisodesPerSeason}
                />
                <p className="mt-1 text-[11px] text-zinc-500">{t('createModal.fieldEpisodesPerSeasonHint')}</p>
              </div>
            </div>
          ) : null}
        </div>

        {error ? <p className="mt-3 text-sm text-rose-300">{error}</p> : null}
      </ProtoModal>

      <AssetMultiPickerModal
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        productionId={productionId}
        selectedUrls={coverUrl ? [coverUrl] : []}
        maxSelect={1}
        onConfirm={(urls) => {
          setCoverUrl(urls[0] ?? '')
          setPickerOpen(false)
        }}
      />
    </>
  )
}
